import { useEffect } from 'react';
import { eventTracker } from 'core/services/EventTracker';
import { BrandingType, type BrandingStatus } from 'types/api/api.types';
import { useFeatureFlagsStore } from 'store/featureFlags/featureFlagsStore';

// The ONLY place that names branding chunks. Every other import() in the app is eager (stays in
// the main bundle, see module.parser.javascript.dynamicImportMode in config/webpack.config.js);
// webpackMode "lazy" opts these out, and the shared webpackChunkName merges each branding's
// imports into one file. A misspelt name here silently creates an extra chunk.
export const halloweenLoaders = {
  BatFlyby: () => import(/* webpackChunkName: "branding-halloween", webpackMode: "lazy" */ '../BatFlyby'),
  HalloweenCat: () =>
    import(/* webpackChunkName: "branding-halloween", webpackMode: "lazy" */ '../HalloweenCat'),
  HalloweenIntro: () =>
    import(/* webpackChunkName: "branding-halloween", webpackMode: "lazy" */ '../HalloweenIntro'),
};

export const christmasLoaders = {
  SnowRain: () => import(/* webpackChunkName: "branding-christmas", webpackMode: "lazy" */ '../SnowRain'),
  ChristmasIntro: () =>
    import(/* webpackChunkName: "branding-christmas", webpackMode: "lazy" */ '../ChristmasIntro'),
};

type LoadersType =
  | typeof halloweenLoaders
  | typeof christmasLoaders;

const loadersByBranding = new Map<BrandingType, LoadersType>([
  ['halloween', halloweenLoaders],
  ['christmas', christmasLoaders],
]);

// Covers a rollout that hasn't reached every pod yet. Webpack forgets a failed chunk, so the
// second call requests it again.
const RETRY_DELAY_MS = 1500;

type LoaderPromiseType = () => Promise<unknown>;

const loadAll = (loaders: LoaderPromiseType[]): Promise<unknown> =>
  Promise.all(loaders.map((loaderPromise) => loaderPromise()));

const wait = (ms: number): Promise<void> => new Promise((resolve) => window.setTimeout(resolve, ms));

const retry = (loaders: LoaderPromiseType[]) => () => wait(RETRY_DELAY_MS).then(() => loadAll(loaders));

const trackLoadingFail =
  (name: BrandingType) =>
  (error: unknown): BrandingStatus => {
    console.warn(`[Branding] "${name}" chunk failed to load, seasonal overlays are off.`, error);
    eventTracker.trackEvent('branding_chunk_failed', { branding: name });
    return 'failed';
  };

/**
 * Downloads a branding's chunk once and keeps it installed for the life of the page. The status
 * lives in featureFlagsStore; any recorded status (including 'loading') makes later calls
 * (re-renders, QA-panel switches back) a no-op. Retries once, then records 'failed'; never
 * rejects. The branding is decorative, so a failure only means it doesn't render.
 */
export const preloadBranding = (name: BrandingType | null): void => {
  const loadersMap = name ? loadersByBranding.get(name) : undefined;
  if (!name || !loadersMap) {
    return;
  }

  const { brandingLoadStatus, setBrandingLoadStatus } = useFeatureFlagsStore.getState();
  if (brandingLoadStatus[name]) {
    return;
  }

  // Set synchronously so concurrent calls see it and don't start a second download.
  setBrandingLoadStatus(name, 'loading');

  const loaders = Object.values(loadersMap);

  void loadAll(loaders)
    .catch(retry(loaders))
    .then((): BrandingStatus => 'ready')
    .catch(trackLoadingFail(name))
    .then((status) => setBrandingLoadStatus(name, status));
};

/**
 * Starts the preload as soon as `name` is known and returns its status. Pass the raw branding
 * flag, not the one filtered by the player's on/off toggle, so switching it back on needs no
 * late chunk request.
 */
export const useBrandingPreload = (name: BrandingType | null): BrandingStatus => {
  useEffect(() => {
    preloadBranding(name);
  }, [name]);

  return useFeatureFlagsStore((state) =>
    !name || !loadersByBranding.has(name) ? 'none' : (state.brandingLoadStatus[name] ?? 'loading'),
  );
};
